// Extract raga values from the header portion of a Namapages song.
// The content itself remains the source of truth; this only creates lookup
// candidates for the optional Raga Studio experience.

const LABEL = /(?:^|\n|\s)(?:r[aā]ga(?:m|ṃ)?|राग(?:म्|ं|म)?|राक(?:म्|ं|म)?|ராக[²³]?(?:ம்)?)[’ʼ']?\s*[:：-]\s*/imu;
// Tala is metadata following the raga value, not another raga candidate.
// Covers tala/thala/thAlam, IAST, Devanagari ताल/ताळ, and Tamil forms.
const STOP = /\s+(?:t[h]?[aā]l(?:\u0324)?(?:am|aṃ|a)?|ताल(?:म्|ं|म)?|ताळ(?:म्|ं|म)?|தாளம்|தாள|தாலம்|தால)\s*[:：-]/iu;

const devanagariConsonants = {
  'क': 'k', 'ख': 'kh', 'ग': 'g', 'घ': 'gh', 'ङ': 'ng', 'च': 'c', 'छ': 'ch', 'ज': 'j', 'झ': 'jh', 'ञ': 'ny',
  'ट': 't', 'ठ': 'th', 'ड': 'd', 'ढ': 'dh', 'ण': 'n', 'त': 't', 'थ': 'th', 'द': 'd', 'ध': 'dh', 'न': 'n',
  'प': 'p', 'फ': 'ph', 'ब': 'b', 'भ': 'bh', 'म': 'm', 'य': 'y', 'र': 'r', 'ल': 'l', 'व': 'v', 'श': 'sh', 'ष': 'sh', 'स': 's', 'ह': 'h', 'ळ': 'l', 'ऴ': 'l', 'ऱ': 'r', 'क़': 'q', 'ख़': 'kh', 'ग़': 'g', 'ज़': 'z', 'ड़': 'd', 'ढ़': 'dh', 'फ़': 'f', 'य़': 'y'
};
const devanagariVowels = { 'अ': 'a', 'आ': 'a', 'इ': 'i', 'ई': 'i', 'उ': 'u', 'ऊ': 'u', 'ऋ': 'ri', 'ए': 'e', 'ऐ': 'ai', 'ओ': 'o', 'औ': 'au', 'ा': 'a', 'ि': 'i', 'ी': 'i', 'ु': 'u', 'ू': 'u', 'ृ': 'ri', 'ॆ': 'e', 'े': 'e', 'ै': 'ai', 'ो': 'o', 'ौ': 'au', 'ॅ': 'e', 'ॉ': 'o' };
const tamilConsonants = { 'க': 'k', 'ங': 'ng', 'ச': 'c', 'ஞ': 'ny', 'ட': 't', 'ண': 'n', 'த': 't', 'ந': 'n', 'ப': 'p', 'ம': 'm', 'ய': 'y', 'ர': 'r', 'ல': 'l', 'வ': 'v', 'ழ': 'zh', 'ள': 'l', 'ற': 'r', 'ன': 'n', 'ஜ': 'j', 'ஷ': 'sh', 'ஸ': 's', 'ஹ': 'h' };
const tamilIndependentVowels = { 'அ': 'a', 'ஆ': 'a', 'இ': 'i', 'ஈ': 'i', 'உ': 'u', 'ஊ': 'u', 'எ': 'e', 'ஏ': 'e', 'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'o', 'ஔ': 'au' };
const tamilVowelSigns = { 'ா': 'a', 'ி': 'i', 'ீ': 'i', 'ு': 'u', 'ூ': 'u', 'ெ': 'e', 'ே': 'e', 'ை': 'ai', 'ொ': 'o', 'ோ': 'o', 'ௌ': 'au' };

// Verified Namapages spellings that differ from the Raga Studio catalog.
// Keep this intentionally small and explicit; it is an alias catalog, not
// approximate fuzzy matching.
const RAGA_ALIAS_GROUPS = [
  ['harikamboji', 'harikambhoji', 'harikampoji'],
  ['bimplas', 'bhimplas', 'bhimaplasa', 'bimaplasa', 'pimaplasa', 'bhimpalas', 'bimpalas', 'bhimpalasi', 'bimpalasi', 'pimplas', 'pimpalas', 'pimpalasi'],
  ['mangalakaishiki', 'mangalakaishika', 'mangalakaisika'],
  ['saranga', 'sarangga', 'sarangaka'],
  ['neelambari', 'nilambari'],
  ['kambhoji', 'kamboji', 'kambodi', 'kambodhi', 'kampodi', 'kampoti'],
  ['bhoopalam', 'bhoopala', 'bhupala', 'bupala', 'pupala'],
  ['kedaragowla', 'kedaragaula', 'kedaragoula', 'ketarakaula'],
  ['madhyamavathi', 'madhyamavati', 'madyamavati', 'matyamavati'],
  ['yadukulakamboji', 'yadukulakambodi', 'yadukulakampodi', 'yadukulakampoti', 'yatukulakampoti'],
  ['atana', 'athana'],
  ['darbar', 'darbhar', 'tarpar'],
  ['kanada', 'kanata'],
  ['begada', 'byakatai', 'pyakatai'],
  ['bageshri', 'bagesri', 'pakeshri', 'pakeri'],
  ['sindhubhairavi', 'sindubhairavi', 'cintupairavi', 'sintupairavi'],
  ['chenjurutti', 'cenjurutti', 'cenyjurutti', 'canjurutti'],
  ['kharaharapriya', 'karaharapriya'],
  ['kunthalavarali', 'kuntalavarali'],
  ['brindavanasaranga', 'pruntavanasaranga', 'brundavanasaranga'],
  ['desh', 'tesh'],
  ['mand', 'mant'],
  ['abheri', 'aperi'],
  ['sanmukhapriya', 'shanmukhapriya', 'sanmukapriya', 'shanmukapriya'],
  ['hamsadhwani', 'hamsadhvani', 'hamsatvani', 'hamsatwani', 'hamsadvani'],
];

function transliterateDevanagari(value) {
  let output = '';
  const chars = Array.from(value);
  for (let i = 0; i < chars.length; i += 1) {
    const char = chars[i];
    if (devanagariConsonants[char]) {
      const next = chars[i + 1];
      output += devanagariConsonants[char];
      if (next === '्') { i += 1; }
      else if (devanagariVowels[next] != null) { output += devanagariVowels[next]; i += 1; }
      else { output += 'a'; }
    } else if (devanagariVowels[char] != null) output += devanagariVowels[char];
    else if (char === 'ं' || char === 'ँ') output += 'm';
    else if (char === 'ः') output += 'h';
    else output += char;
  }
  return output;
}

function transliterateTamil(value) {
  let output = '';
  const chars = Array.from(value);
  for (let i = 0; i < chars.length; i += 1) {
    const char = chars[i];
    if (tamilConsonants[char]) {
      const next = chars[i + 1];
      output += tamilConsonants[char];
      if (tamilVowelSigns[next] != null) { output += tamilVowelSigns[next]; i += 1; }
      else if (next === '்') { i += 1; }
      else output += 'a';
    } else if (tamilIndependentVowels[char] != null) output += tamilIndependentVowels[char];
    else output += char;
  }
  return output;
}

export function ragaLookupKey(value) {
  const transliterated = transliterateTamil(transliterateDevanagari(value));
  return transliterated.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase()
    // Common Namapages / ITRANS spelling differences used by the catalog.
    .replace(/ngk/g, 'nk')
    .replace(/ngg/g, 'ng')
    .replace(/shankara/g, 'sankara')
    .replace(/hindola/g, 'hintola')
    // Tamil \u0bb8\u0bbe\u0bb0\u0ba9\u0bcd\u0b95\u0bbe (sArankA) denotes Saranga.  Keep this
    // correction at the normalized-key layer so it also works in compound names.
    .replace(/saranka/g, 'saranga')
    .replace(/aa/g, 'a');
}

// Raga catalogs commonly use either `gauri` or `gouri` (and similarly
// `gowri`). Keep these controlled spelling alternatives separate from the
// canonical key so that we never guess from broad fuzzy text matching.
export function ragaLookupVariants(value) {
  const key = ragaLookupKey(value);
  const variants = new Set([key]);
  RAGA_ALIAS_GROUPS.forEach(group => {
    if (group.includes(key)) group.forEach(alias => variants.add(alias));
  });
  if (key.includes('gauri')) {
    variants.add(key.replace(/gauri/g, 'gouri'));
    variants.add(key.replace(/gauri/g, 'gowri'));
  }
  if (key.includes('gouri')) variants.add(key.replace(/gouri/g, 'gauri'));
  if (key.includes('gowri')) variants.add(key.replace(/gowri/g, 'gauri'));
  // Tamil கௌரி is transliterated as kauri by the lightweight client
  // transliterator, but catalogues commonly index the raga under Gauri/Gouri.
  if (key.startsWith('kauri')) {
    variants.add(key.replace(/^kauri/, 'gauri'));
    variants.add(key.replace(/^kauri/, 'gouri'));
    variants.add(key.replace(/^kauri/, 'gowri'));
  }
  // Sanskrit/Tamil source files commonly omit the aspirate in Vasantha.
  if (key.endsWith('vasanta')) variants.add(`${key.slice(0, -7)}vasantha`);
  if (key.endsWith('vasantha')) variants.add(`${key.slice(0, -8)}vasanta`);
  return [...variants];
}

function splitCandidates(value) {
  const candidates = value
    .replace(/(?:\[|\]|\(|\))/g, '|')
    .split(/\s*(?:\/|,|;|&|\band\b|\|)\s*/iu)
    .map(item => item.trim())
    .filter(Boolean);
  // Ashtapadhi uses this unpunctuated paired declaration. It means two ragas,
  // not one long catalog name; the IAST edition additionally contains "ṭṭa".
  return candidates.flatMap(item => {
    const key = ragaLookupKey(item);
    return key.includes('bilahari') && (key.includes('desakshi') || key.includes('deshakshi') || key.includes('desaksi'))
      ? ['Bilahari', 'Desakshi']
      : [item];
  });
}

export function extractSongRagas(content) {
  if (!content) return [];
  // MIX files commonly place equivalent Sanskrit and Tamil metadata lines at
  // the start of the song. Read the header region so language-specific extra
  // ragas are not discarded, while avoiding lyric text farther down the song.
  const header = content.slice(0, 2048);
  const labels = [...header.matchAll(new RegExp(LABEL.source, 'gimu'))];
  if (!labels.length) return [];
  const candidates = labels.flatMap(label => {
    const afterLabel = header.slice((label.index || 0) + label[0].length);
    const firstLine = afterLabel.split(/\r?\n/, 1)[0];
    const rawValue = firstLine.split(STOP, 1)[0].replace(/\s*[-–—]\s*$/, '').trim();
    return rawValue ? splitCandidates(rawValue) : [];
  });

  const seen = new Set();
  return candidates.filter(name => {
    const key = ragaLookupKey(name);
    const canonicalKey = RAGA_ALIAS_GROUPS.find(group => group.includes(key))?.[0] || key;
    if (!key || seen.has(canonicalKey)) return false;
    seen.add(canonicalKey);
    return true;
  }).map(sourceName => ({ sourceName, lookupKey: ragaLookupKey(sourceName) }));
}
